import { DataGrid } from 'src/feature/data-grid/data-grid';
import {
  DEFAULT_DATA_GRID_MODEL,
  DEFAULT_PAGE_SIZE,
} from 'src/config/data-grid.config';
import { Snackbar } from 'src/shared/snackbar/snackbar';
import { useTranslation } from 'react-i18next';

import { useGetCustomersPaginatedQuery } from '@service/customer.service';
import { useCallback, useState } from 'react';
import { SortOrder } from '@delmonicos/shared-sdk/libs/emsp/src/libs/core/src/utils/mongo/pagination/models/pagination.model';
import { GetEmspUsersModel } from '@delmonicos/shared-sdk/libs/emsp/src/libs/back-office/src/models/emsp-users/get-emsp-users.model';
import { DataGridQueryModel } from '@model/data-grid.model';
import { useCustomerFilter } from './use-customer-filter';
import { useCustomerColumn } from './use-customer-column';
import { useDataGridSearchParams } from '../../data-grid/use-data-grid-search-params/use-data-grid-search-params';

export function CustomerDataGrid(): React.JSX.Element {
  const { t } = useTranslation();
  const columns = useCustomerColumn();

  // PAGINATION & FILTERS //////////////////////////////////////////////////////
  const filterDefinitionModel = useCustomerFilter();

  const { model: urlModel } = useDataGridSearchParams(DEFAULT_DATA_GRID_MODEL);
  const [model, setModel] = useState<DataGridQueryModel>(urlModel);

  const [params, setParams] = useState<GetEmspUsersModel>(() => {
    const pageSize =
      DEFAULT_DATA_GRID_MODEL.paginate?.pageSize ?? DEFAULT_PAGE_SIZE;
    const page = DEFAULT_DATA_GRID_MODEL.paginate?.page ?? 0;

    return {
      sortField: 'lastConnection',
      sortOrder: SortOrder.DESC,
      limit: pageSize,
      offset: page * pageSize,
      textSearch: urlModel.textSearch || undefined,
      pseudo: urlModel.filter?.pseudo?.toString() || undefined,
      lastName: urlModel.filter?.lastName?.toString() || undefined,
      firstName: urlModel.filter?.firstName?.toString() || undefined,
      emailAddress: urlModel.filter?.emailAddress?.toString() || undefined,
    };
  });

  const onModelChange = useCallback(
    (newModel: DataGridQueryModel) => {
      setModel(newModel);

      setParams((prev) => {
        const pageSize =
          newModel.paginate?.pageSize ?? prev.limit ?? DEFAULT_PAGE_SIZE;
        const page = newModel.paginate?.page ?? 0;

        return {
          ...prev,
          limit: pageSize,
          offset: page * pageSize,
          textSearch: newModel.textSearch || undefined,
          pseudo: newModel.filter?.pseudo?.toString() || undefined,
          lastName: newModel.filter?.lastName?.toString() || undefined,
          firstName: newModel.filter?.firstName?.toString() || undefined,
          emailAddress: newModel.filter?.emailAddress?.toString() || undefined,
        };
      });
    },

    [setModel],
  );

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const {
    data: paginatedData,
    isFetching,
    isError,
  } = useGetCustomersPaginatedQuery(params);

  return (
    <>
      <DataGrid
        columns={columns}
        filterDefinitionModel={filterDefinitionModel}
        loading={isFetching}
        rows={paginatedData?.docs ?? []}
        rowCount={paginatedData?.totalDocs ?? 0}
        model={model}
        onModelChange={onModelChange}
      />

      {isError && (
        <Snackbar
          variant="error"
          message={t('error.get-list-customer-query')}
        />
      )}
    </>
  );
}
